// Pure colour helpers (no dependencies). All maths is done in JS so the output is
// byte-identical on browsers without CSS color-mix() support (older Safari).
//
// Colour objects are plain { r, g, b, a } with r/g/b in 0..255 and a in 0..1.

const NAMED = {
    transparent: { r: 0, g: 0, b: 0, a: 0 },
    white: { r: 255, g: 255, b: 255, a: 1 },
    black: { r: 0, g: 0, b: 0, a: 1 }
}

const clamp01 = (n) => Math.min(1, Math.max(0, n))
const clamp255 = (n) => Math.min(255, Math.max(0, n))

function parseChannel(token) {
    const t = token.trim()
    if (t === '') return null
    if (t.endsWith('%')) {
        const pct = parseFloat(t)
        return Number.isFinite(pct) ? clamp255(pct * 2.55) : null
    }
    const n = parseFloat(t)
    return Number.isFinite(n) ? clamp255(n) : null
}

function parseAlpha(token) {
    if (token === undefined || token === null) return 1
    const t = String(token).trim()
    if (t === '') return 1
    if (t.endsWith('%')) {
        const pct = parseFloat(t)
        return Number.isFinite(pct) ? clamp01(pct / 100) : null
    }
    const n = parseFloat(t)
    return Number.isFinite(n) ? clamp01(n) : null
}

/**
 * parseColor('#rgb' | '#rgba' | '#rrggbb' | '#rrggbbaa' | 'rgb(...)' | 'rgba(...)'
 *            | 'transparent' | 'white' | 'black') -> { r, g, b, a } | null
 * rgb()/rgba() accept comma-separated, space-separated and "/ alpha" syntax.
 */
export function parseColor(str) {
    if (typeof str !== 'string') return null
    const s = str.trim().toLowerCase()
    if (!s) return null

    if (NAMED[s]) return { ...NAMED[s] }

    if (s[0] === '#') {
        const hex = s.slice(1)
        if (!/^[0-9a-f]+$/.test(hex)) return null
        if (hex.length === 3 || hex.length === 4) {
            const r = parseInt(hex[0] + hex[0], 16)
            const g = parseInt(hex[1] + hex[1], 16)
            const b = parseInt(hex[2] + hex[2], 16)
            const a = hex.length === 4 ? parseInt(hex[3] + hex[3], 16) / 255 : 1
            return { r, g, b, a }
        }
        if (hex.length === 6 || hex.length === 8) {
            const r = parseInt(hex.slice(0, 2), 16)
            const g = parseInt(hex.slice(2, 4), 16)
            const b = parseInt(hex.slice(4, 6), 16)
            const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1
            return { r, g, b, a }
        }
        return null
    }

    const fn = /^rgba?\(\s*(.+?)\s*\)$/.exec(s)
    if (fn) {
        let body = fn[1]
        let alphaToken
        const slash = body.indexOf('/')
        if (slash !== -1) {
            alphaToken = body.slice(slash + 1)
            body = body.slice(0, slash)
        }
        const parts = body.split(/[\s,]+/).filter(Boolean)
        if (parts.length < 3 || parts.length > 4) return null
        if (parts.length === 4) {
            if (alphaToken !== undefined) return null
            alphaToken = parts.pop()
        }
        const r = parseChannel(parts[0])
        const g = parseChannel(parts[1])
        const b = parseChannel(parts[2])
        const a = parseAlpha(alphaToken)
        if (r === null || g === null || b === null || a === null) return null
        return { r, g, b, a }
    }

    return null
}

const round = (n) => Math.round(n)
const roundA = (a) => Math.round(a * 1000) / 1000

/** toCss({r,g,b,a}) -> 'rgb(r, g, b)' when a === 1, otherwise 'rgba(r, g, b, a)' */
export function toCss(c) {
    if (!c) return ''
    const r = round(c.r), g = round(c.g), b = round(c.b)
    const a = c.a === undefined ? 1 : roundA(c.a)
    return a >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${a})`
}

/** Alpha-composite fg over an opaque bg. Returns an opaque colour. */
export function flatten(fg, bg) {
    if (!fg) return bg ? { ...bg, a: 1 } : null
    if (!bg) return { ...fg }
    const a = fg.a === undefined ? 1 : fg.a
    return {
        r: fg.r * a + bg.r * (1 - a),
        g: fg.g * a + bg.g * (1 - a),
        b: fg.b * a + bg.b * (1 - a),
        a: 1
    }
}

/** sRGB linear interpolation of r, g, b, a. t = weight of c2 (0..1). */
export function mix(c1, c2, t) {
    if (!c1) return c2 ? { ...c2 } : null
    if (!c2) return { ...c1 }
    const w = clamp01(t)
    const a1 = c1.a === undefined ? 1 : c1.a
    const a2 = c2.a === undefined ? 1 : c2.a
    return {
        r: c1.r + (c2.r - c1.r) * w,
        g: c1.g + (c2.g - c1.g) * w,
        b: c1.b + (c2.b - c1.b) * w,
        a: a1 + (a2 - a1) * w
    }
}

function linearise(channel) {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/** WCAG relative luminance of an opaque colour (alpha is ignored). */
export function luminance(c) {
    if (!c) return 0
    return 0.2126 * linearise(c.r) + 0.7152 * linearise(c.g) + 0.0722 * linearise(c.b)
}

/**
 * WCAG contrast ratio (Lmax + .05) / (Lmin + .05).
 * c2 is treated as the background; a translucent c1 is flattened over it first.
 */
export function contrast(c1, c2) {
    if (!c1 || !c2) return 1
    const bg = c2.a !== undefined && c2.a < 1 ? flatten(c2, NAMED.white) : c2
    const fg = c1.a !== undefined && c1.a < 1 ? flatten(c1, bg) : c1
    const l1 = luminance(fg)
    const l2 = luminance(bg)
    const hi = Math.max(l1, l2)
    const lo = Math.min(l1, l2)
    return (hi + 0.05) / (lo + 0.05)
}

/** The candidate with the highest contrast against bg (null entries are skipped). */
export function pickOn(bg, candidates) {
    let best = null
    let bestRatio = -1
    for (const cand of candidates || []) {
        if (!cand) continue
        const ratio = contrast(cand, bg)
        if (ratio > bestRatio) {
            bestRatio = ratio
            best = cand
        }
    }
    return best
}

/** True when white text reads better on c than black text does. */
export function isDark(c) {
    if (!c) return false
    return contrast(NAMED.white, c) >= contrast(NAMED.black, c)
}

export const WHITE = { ...NAMED.white }
export const BLACK = { ...NAMED.black }
